import { useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  api,
  type AssessmentRunStatus,
  type AssessmentRunFailureCategory,
} from "../../lib/api";
import { buildUrl } from "../../lib/router";
import { useConfirmDialog } from "../../components/ConfirmDialog.helpers";

const FAILURE_CATEGORY_LABEL: Record<
  AssessmentRunFailureCategory,
  string
> = {
  missing_measure: "LLM response missing measure(s)",
  invalid_json: "LLM returned invalid JSON",
  content_filter: "Blocked by provider safety filter",
  llm_api_error: "LLM API error / timeout",
  other: "Other",
};

export function AssessmentRunReportCard({
  run,
  hasActiveRun,
}: {
  run: AssessmentRunStatus;
  hasActiveRun: boolean;
}) {
  const s = run.summary;
  const qc = useQueryClient();
  const dialog = useConfirmDialog();
  const [showFailedPairs, setShowFailedPairs] = useState(false);
  const [showAllPairs, setShowAllPairs] = useState(false);

  const retryM = useMutation({
    mutationFn: async () =>
      api.assessmentRunRetryFailed(run.run_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessment-run-active"] });
      qc.invalidateQueries({ queryKey: ["assessment-run-latest"] });
      qc.invalidateQueries({ queryKey: ["assessment-runs-history"] });
      qc.invalidateQueries({ queryKey: ["assessment-run-by-window"] });
    },
  });
  const retryErr = retryM.error as { message?: string } | null;

  async function handleRetryClick() {
    if (s.failed <= 0 || hasActiveRun || retryM.isPending) return;
    const ok = await dialog.confirm({
      title: "Retry failed pairs?",
      message: `Retry ${s.failed} failed pair${s.failed === 1 ? "" : "s"} from this run? Successful and skipped pairs are left untouched.`,
      confirmLabel: "Retry failed pairs",
    });
    if (!ok) return;
    try {
      await retryM.mutateAsync();
    } catch {
      /* surfaced via retryErr */
    }
  }

  const pageLimit = showAllPairs ? 200 : 50;
  const failedQ = useQuery({
    queryKey: ["assessment-run-failed-pairs", run.run_id, pageLimit],
    queryFn: () =>
      api.assessmentRunFailedPairs(
        run.run_id,
        pageLimit,
        0,
      ),
    enabled: showFailedPairs && (s.failed ?? 0) > 0,
    placeholderData: keepPreviousData,
  });

  const fmtTokens = (n: number) => n.toLocaleString();
  const fmtAvg = (n: number | null) =>
    n == null ? "—" : Math.round(n).toLocaleString();
  const skippedAssessments = s.per_assessment.filter((a) => a.skipped > 0);
  const totalTokens = s.input_tokens + s.output_tokens;

  return (
    <div
      className={`rounded border p-3 text-sm space-y-3 ${
        run.status === "completed"
          ? "border-border bg-card"
          : run.status === "partial"
            ? "border-amber-500/40 bg-amber-500/10"
            : run.status === "cancelled"
              ? "border-border bg-muted/30"
              : "border-destructive/40 bg-destructive/10"
      }`}
    >
      <div className="font-medium flex items-baseline gap-2">
        <span>
          {run.status === "completed"
            ? "Run complete"
            : run.status === "partial"
              ? "Run partially complete"
              : run.status === "cancelled"
                ? "Run cancelled"
                : run.terminal_reason === "lease_lost"
                  ? "Run failed — lease lost"
                  : "Run failed"}
        </span>
        {run.sync_window_id ? (
          <span className="text-xs text-muted-foreground font-normal">
            · auto-chained after Sync
          </span>
        ) : run.source_run_id ? (
          <span className="text-xs text-muted-foreground font-normal">
            · retry of {run.source_run_id.slice(0, 8)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground font-normal">
            · manual run
          </span>
        )}
      </div>

      {/* Headline counts */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground">
        <div>
          Attempted{" "}
          <span className="text-foreground tabular-nums">{s.attempted}</span>
        </div>
        <div>
          Succeeded{" "}
          <span className="text-foreground tabular-nums">{s.succeeded}</span>
        </div>
        <div>
          Failed{" "}
          <span
            className={
              s.failed > 0
                ? "text-destructive tabular-nums"
                : "text-foreground tabular-nums"
            }
          >
            {s.failed}
          </span>
        </div>
        <div>
          Skipped{" "}
          <span className="text-foreground tabular-nums">{s.skipped}</span>
        </div>
        <div>
          Model{" "}
          <span className="font-mono text-xs text-foreground">
            {s.model ?? "—"}
          </span>
        </div>
        <div>
          Duration{" "}
          <span className="text-foreground tabular-nums">
            {s.duration_ms != null
              ? `${(s.duration_ms / 1000).toFixed(1)}s`
              : "—"}
          </span>
        </div>
      </div>

      {run.error && (
        <div className="text-xs text-destructive">{run.error}</div>
      )}

      {/* Skipped breakdown */}
      {s.skipped > 0 && (
        <div className="text-xs text-muted-foreground space-y-0.5">
          {skippedAssessments.length === 0 ? (
            <div>
              {s.skipped} pair{s.skipped === 1 ? "" : "s"} skipped — already
              assessed in window.
            </div>
          ) : (
            <>
              <div className="font-medium text-foreground/80">
                Skipped — already assessed in window
              </div>
              <ul className="ml-3 list-disc">
                {skippedAssessments.map((a) => (
                  <li key={a.assessment_id}>
                    {a.assessment_title}:{" "}
                    <span className="tabular-nums">{a.skipped}</span> skipped
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Token breakdown */}
      <div className="text-xs space-y-0.5">
        <div className="font-medium text-foreground/80">Tokens</div>
        <div className="text-muted-foreground">
          Total{" "}
          <span className="tabular-nums text-foreground">
            {fmtTokens(s.input_tokens)} in / {fmtTokens(s.output_tokens)} out
          </span>{" "}
          ({fmtTokens(totalTokens)} combined)
        </div>
        <div className="text-muted-foreground">
          Avg per successful pair{" "}
          <span className="tabular-nums text-foreground">
            {fmtAvg(s.avg_input_tokens)} in / {fmtAvg(s.avg_output_tokens)} out
          </span>
        </div>
        {s.per_model.length > 1 && (
          <ul className="ml-3 list-disc text-muted-foreground">
            {s.per_model.map((m) => (
              <li key={m.model ?? "(none)"}>
                <span className="font-mono text-foreground">
                  {m.model ?? "(none)"}
                </span>
                :{" "}
                <span className="tabular-nums text-foreground">
                  {fmtTokens(m.input_tokens)} in /{" "}
                  {fmtTokens(m.output_tokens)} out
                </span>{" "}
                · {m.succeeded} succeeded, {m.failed} failed
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Failure breakdown by category */}
      {s.failure_categories.length > 0 && (
        <div className="text-xs space-y-0.5">
          <div className="font-medium text-foreground/80">Failures</div>
          <ul className="ml-3 list-disc text-muted-foreground">
            {s.failure_categories.map((c) => (
              <li key={c.category}>
                {FAILURE_CATEGORY_LABEL[c.category]}:{" "}
                <span className="tabular-nums text-foreground">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-assessment rollup */}
      {s.per_assessment.length > 0 && (
        <div className="text-xs space-y-1">
          <div className="font-medium text-foreground/80">
            Per-assessment breakdown
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border-collapse">
              <thead>
                <tr className="text-muted-foreground text-left">
                  <th className="pr-3 font-normal">Assessment</th>
                  <th className="px-2 font-normal text-right">Attempted</th>
                  <th className="px-2 font-normal text-right">Succeeded</th>
                  <th className="px-2 font-normal text-right">Failed</th>
                  <th className="px-2 font-normal text-right">Skipped</th>
                  <th className="px-2 font-normal text-right">In tokens</th>
                  <th className="pl-2 font-normal text-right">Out tokens</th>
                </tr>
              </thead>
              <tbody>
                {s.per_assessment.map((a) => (
                  <tr
                    key={a.assessment_id}
                    className="border-t border-border/60"
                  >
                    <td className="pr-3 py-1">{a.assessment_title}</td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {a.attempted}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {a.succeeded}
                    </td>
                    <td
                      className={`px-2 py-1 text-right tabular-nums ${
                        a.failed > 0 ? "text-destructive" : ""
                      }`}
                    >
                      {a.failed}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {a.skipped}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">
                      {fmtTokens(a.input_tokens)}
                    </td>
                    <td className="pl-2 py-1 text-right tabular-nums">
                      {fmtTokens(a.output_tokens)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Failed pairs (lazy) */}
      {s.failed > 0 && (
        <div className="text-xs space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="text-foreground/80 underline-offset-2 hover:underline"
              onClick={() => setShowFailedPairs((v) => !v)}
            >
              {showFailedPairs ? "Hide" : "Show"} failed pairs ({s.failed})
            </button>
            <button
              type="button"
              className="rounded border border-border bg-background px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleRetryClick}
              disabled={hasActiveRun || retryM.isPending}
              title={
                hasActiveRun
                  ? "Phase 3 already running — wait for it to finish before triggering a retry."
                  : undefined
              }
            >
              {retryM.isPending ? "Retrying…" : "Retry failed pairs"}
            </button>
            {hasActiveRun && (
              <span className="text-muted-foreground">
                Phase 3 already running — wait for it to finish before
                triggering a retry.
              </span>
            )}
            {retryErr && (
              <span className="text-destructive">
                {retryErr.message ?? "Retry failed"}
              </span>
            )}
          </div>
          {showFailedPairs && (
            <div className="border border-border rounded p-2 space-y-1 bg-background/30">
              {failedQ.isLoading && (
                <div className="text-muted-foreground">Loading…</div>
              )}
              {failedQ.error && (
                <div className="text-destructive">
                  Couldn't load failed pairs:{" "}
                  {(failedQ.error as { message?: string }).message ?? "error"}
                </div>
              )}
              {failedQ.data && failedQ.data.items.length === 0 && (
                <div className="text-muted-foreground">No failed pairs.</div>
              )}
              {failedQ.data && failedQ.data.items.length > 0 && (
                <>
                  <table className="min-w-full text-xs border-collapse">
                    <thead>
                      <tr className="text-muted-foreground text-left">
                        <th className="pr-3 font-normal">Call</th>
                        <th className="pr-3 font-normal">Assessment</th>
                        <th className="pr-3 font-normal">Category</th>
                        <th className="pr-3 font-normal">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedQ.data.items.map((p) => (
                        <tr
                          key={`${p.call_id}-${p.assessment_id}`}
                          className="border-t border-border/60 align-top"
                        >
                          <td className="pr-3 py-1">
                            <a
                              href={buildUrl(
                                "call-detail",
                                null,
                                null,
                                { callId: p.call_id },
                              )}
                              className="text-primary hover:underline font-mono"
                            >
                              {p.call_id}
                            </a>
                          </td>
                          <td className="pr-3 py-1">{p.assessment_title}</td>
                          <td className="pr-3 py-1 text-muted-foreground">
                            {FAILURE_CATEGORY_LABEL[p.error_category]}
                          </td>
                          <td className="pr-3 py-1 text-muted-foreground break-all">
                            {p.error ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {failedQ.data.total > failedQ.data.items.length &&
                    !showAllPairs && (
                      <button
                        type="button"
                        className="text-foreground/80 underline-offset-2 hover:underline"
                        onClick={() => setShowAllPairs(true)}
                      >
                        Show more (up to 200) — total {failedQ.data.total}
                      </button>
                    )}
                  {showAllPairs &&
                    failedQ.data.total > failedQ.data.items.length && (
                      <div className="text-muted-foreground">
                        Showing first {failedQ.data.items.length} of{" "}
                        {failedQ.data.total}.
                      </div>
                    )}
                </>
              )}
            </div>
          )}
        </div>
      )}
      {dialog.element}
    </div>
  );
}
